/**
 * Utility functions for calculating employee status
 * Status: Active (present), On Leave, Absent, or Holiday
 */

/**
 * Determine employee status for a given date
 * @param {Object} attendance - Attendance record for the date
 * @param {Object[]} holidays - Array of holiday records
 * @param {Object[]} leaveRequests - Array of approved leave requests for the date
 * @param {string} dateStr - Date string "YYYY-MM-DD"
 * @returns {string} - "Active", "On Leave", "Absent", or "Holiday"
 */
export function calculateEmployeeStatus(
  attendance,
  holidays,
  leaveRequests,
  dateStr,
) {
  // Check if date is a holiday
  const isHoliday = holidays.some((h) => h.date === dateStr && !h.isOptional);
  if (isHoliday) {
    return "Holiday";
  }

  // Check if employee has approved leave for this date
  const hasApprovedLeave = leaveRequests.some((lr) => {
    if (lr.status !== "approved") return false;
    const fromDate = new Date(lr.fromDate);
    const toDate = new Date(lr.toDate);
    const checkDate = new Date(dateStr);
    return checkDate >= fromDate && checkDate <= toDate;
  });

  if (hasApprovedLeave) {
    return "On Leave";
  }

  // Check if attendance record exists with valid check-in
  if (attendance && attendance.checkIn) {
    return "Active";
  }

  // Otherwise, mark as absent
  return "Absent";
}

/**
 * Get status badge styling
 * @param {string} status - Status string
 * @returns {Object} - { backgroundColor, textColor, label }
 */
export function getStatusBadgeStyle(status) {
  const styles = {
    Active: {
      backgroundColor: "#10b981",
      textColor: "white",
      label: "✓ Active",
    },
    "On Leave": {
      backgroundColor: "#f59e0b",
      textColor: "white",
      label: "🌴 On Leave",
    },
    Absent: {
      backgroundColor: "#ef4444",
      textColor: "white",
      label: "✗ Absent",
    },
    Holiday: {
  backgroundColor: "#8b5cf6",
  textColor: "white",
  label: (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.35rem",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        className="size-6"
        style={{
          width: "1rem",
          height: "1rem",
        }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
        />
      </svg>

      Holiday
    </span>
  ),
},
  };

  return styles[status] || styles.Absent;
}

/**
 * Calculate attendance statistics for a month
 * @param {Object[]} attendanceRecords - Array of attendance records for the month
 * @param {Object[]} holidays - Array of all holidays
 * @param {Object[]} leaveRequests - Array of approved leave requests
 * @param {number} year
 * @param {number} month
 * @returns {Object} - { present, absent, leave, holiday, working_days }
 */
export function calculateMonthlyStats(
  attendanceRecords,
  holidays,
  leaveRequests,
  year,
  month,
) {
  const monthStr = String(month).padStart(2, "0");
  const now = new Date();
  const isCurrentMonth =
    now.getFullYear() === year && now.getMonth() + 1 === month;

  let present = 0;
  let absent = 0;
  let onLeave = 0;
  let holiday = 0;
  let workingDays = 0;

  // Get the last day of the month
  const lastDay = isCurrentMonth
    ? now.getDate()
    : new Date(year, month, 0).getDate();

  // Iterate through each day in the month
  for (let d = 1; d <= lastDay; d++) {
    const dd = String(d).padStart(2, "0");
    const dateStr = `${year}-${monthStr}-${dd}`;
    const dayOfWeek = new Date(year, month - 1, d).getDay();

    // Skip weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      continue;
    }

    workingDays++;

    // Find attendance record for this date
    const attRec = attendanceRecords.find((a) => a.date === dateStr);

    // Check status
    const status = calculateEmployeeStatus(
      attRec,
      holidays,
      leaveRequests,
      dateStr,
    );

    switch (status) {
      case "Active":
        present++;
        break;
      case "On Leave":
        onLeave++;
        break;
      case "Holiday":
        holiday++;
        workingDays--; // Don't count holidays in working days
        break;
      case "Absent":
      default:
        absent++;
        break;
    }
  }

  return {
    present,
    absent,
    onLeave,
    holiday,
    workingDays,
  };
}

/**
 * Generate status report for all employees
 * @param {Object[]} employees - Array of employee records
 * @param {Object[]} attendanceRecords - Attendance records for the date
 * @param {Object[]} holidays - Array of holidays
 * @param {Object[]} leaveRequests - Array of leave requests
 * @param {string} dateStr - Date string "YYYY-MM-DD"
 * @returns {Object[]} - Array of { userId, employeeId, displayName, status, checkInTime }
 */
export function generateDailyStatusReport(
  employees,
  attendanceRecords,
  holidays,
  leaveRequests,
  dateStr,
) {
  return employees.map((emp) => {
    const attRec = attendanceRecords.find((a) => a.userId === emp.uid);
    const empLeaveRequests = leaveRequests.filter(
      (lr) => lr.userId === emp.uid,
    );

    const status = calculateEmployeeStatus(
      attRec,
      holidays,
      empLeaveRequests,
      dateStr,
    );

    return {
      userId: emp.uid,
      employeeId: emp.employeeId,
      displayName: emp.displayName,
      department: emp.department,
      status,
      checkInTime: attRec ? attRec.checkIn : null,
      checkOutTime: attRec ? attRec.checkOut : null,
      hoursWorked: attRec ? attRec.hoursWorked : 0,
    };
  });
}

/**
 * Reusable function to calculate employee stats over a date range
 */
export function calculateEmployeeStatsForRange({
  attendanceRecords,
  leaveRequests,
  holidays,
  startDate,
  endDate,
  todayStr
}) {
  let presentCount = 0;
  let absentCount = 0;
  let leavesTakenCount = 0;
  let workingDaysCount = 0;

  const holidayDates = new Set(holidays.map((h) => h.date));
  const attMap = {};
  attendanceRecords.forEach((a) => {
    if (a.date) attMap[a.date] = a;
  });

  let curr = new Date(startDate + "T00:00:00");
  const stop = new Date(endDate + "T00:00:00");

  const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  while (curr <= stop) {
    const dateStr = formatDateLocal(curr);
    const dayOfWeek = curr.getDay();

    if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dateStr)) {
      const att = attMap[dateStr];
      const isUserPresent = att && !!att.checkIn;

      if (isUserPresent) {
        presentCount++;
        workingDaysCount++;
      } else {
        const leavesCovering = leaveRequests.filter(
          (lr) => lr.fromDate <= dateStr && lr.toDate >= dateStr
        );
        const hasApproved = leavesCovering.some((lr) => lr.status === "approved");
        const hasPending = leavesCovering.some(
          (lr) => lr.status === "pending_lead" || lr.status === "pending_admin"
        );
        const hasRejectedOrCancelled = leavesCovering.some(
          (lr) => lr.status === "rejected" || lr.status === "cancelled"
        );

        if (hasApproved) {
          absentCount++;
          leavesTakenCount++;
          workingDaysCount++;
        } else if (hasPending || hasRejectedOrCancelled) {
          // Ignore
        } else {
          if (dateStr <= todayStr) {
            absentCount++;
            workingDaysCount++;
          }
        }
      }
    }
    curr.setDate(curr.getDate() + 1);
  }

  const rate =
    workingDaysCount > 0
      ? Math.round((presentCount / workingDaysCount) * 100)
      : 0;

  return {
    present: presentCount,
    absent: absentCount,
    leaves: leavesTakenCount,
    rate,
  };
}

/**
 * Reusable function to calculate daily stats for a group of employees
 */
export function calculateDailyStatsForEmployees({
  employees,
  attendanceRecords,
  leaveRequests,
  isHolidayToday,
  dateStr
}) {
  let presentCount = 0;
  let absentCount = 0;
  let onLeaveCount = 0;

  const dayOfWeek = new Date(dateStr + "T00:00:00").getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  if (isWeekend || isHolidayToday) {
    return {
      present: 0,
      absent: 0,
      leaves: 0,
      rate: 0,
      eligibleHeadcount: 0,
    };
  }

  employees.forEach((emp) => {
    const att = attendanceRecords.find((a) => a.userId === emp.uid);
    const isUserPresent = att && !!att.checkIn;

    if (isUserPresent) {
      presentCount++;
    } else {
      const userLeaves = leaveRequests.filter(
        (lr) =>
          lr.userId === emp.uid &&
          lr.fromDate <= dateStr &&
          lr.toDate >= dateStr
      );
      const hasApproved = userLeaves.some((lr) => lr.status === "approved");
      const hasPending = userLeaves.some(
        (lr) => lr.status === "pending_lead" || lr.status === "pending_admin"
      );
      const hasRejectedOrCancelled = userLeaves.some(
        (lr) => lr.status === "rejected" || lr.status === "cancelled"
      );

      if (hasApproved) {
        absentCount++;
        onLeaveCount++;
      } else if (hasPending || hasRejectedOrCancelled) {
        // Ignore
      } else {
        absentCount++;
      }
    }
  });

  const eligibleHeadcount = presentCount + absentCount;
  const rate =
    eligibleHeadcount > 0
      ? Math.round((presentCount / eligibleHeadcount) * 100)
      : 0;

  return {
    present: presentCount,
    absent: absentCount,
    leaves: onLeaveCount,
    rate,
    eligibleHeadcount,
  };
}
